import { useEffect, useMemo, useRef, useState } from "react";
import { DraftRevenueItem } from "@/pages/contracts/components/termsDraft/types";
import { ContractData } from "@/pages/contracts/types";
import { useTermsPreview } from "./hooks";
import { toPreviewItems } from "./toPreviewItems";
import {
  TermsCalculatedValue,
  TermsCalculatedValues,
  TermsChangeSet,
  TermsUpdateKind
} from "./types";

const PREVIEW_DEBOUNCE_MS = 400;

const persisted = (before: number | null): TermsCalculatedValue => ({
  before,
  after: before,
  change: null
});

// Until the first preview lands, both columns show the persisted figures.
const fromContract = (
  contract: ContractData | undefined
): TermsCalculatedValues => ({
  mrr: persisted(contract?.calculatedMrrLocal ?? null),
  cmrr: persisted(contract?.calculatedCmrrLocal ?? null),
  annualContractValue: persisted(
    contract?.calculatedAnnualContractValueLocal ?? null
  ),
  totalContractValue: persisted(
    contract?.calculatedTotalContractValueLocal ?? null
  ),
  arr: persisted(contract?.calculatedArrLocal ?? null),
  carr: persisted(contract?.calculatedCarrLocal ?? null),
  currency: contract?.currency ?? null
});

// The footer's Current / New figures, debounced because every keystroke changes the set.
export const useTermsCalculatedValues = (
  contractId: string | undefined,
  contract: ContractData | undefined,
  items: DraftRevenueItem[],
  updateKind: TermsUpdateKind,
  effectiveDate: string | null = null,
  includeNote = false
) => {
  const preview = useTermsPreview(contractId);
  // The mutation object is a fresh ref every render; keep it out of the deps.
  const previewRef = useRef(preview);
  // Assigned after commit, not during render — a discarded render must not be
  // what the debounce timer below ends up firing.
  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  const [values, setValues] = useState<TermsCalculatedValues | null>(null);
  const [changeSet, setChangeSet] = useState<TermsChangeSet | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // A failed preview leaves the last good figures on screen; without this they
  // would read as current for the edited set they no longer describe.
  const [isStale, setIsStale] = useState(false);
  // Before the first success the fallback figures are the contract's own, not a
  // stale preview — a failure then has nothing to invalidate.
  const hasSucceededRef = useRef(false);

  // One contract's figures must never be served for another. The page also keys
  // the editor on the contract, so this is the hook defending itself in isolation.
  useEffect(() => {
    setValues(null);
    setChangeSet(null);
    setIsStale(false);
    hasSucceededRef.current = false;
  }, [contractId]);

  const requestBody = useMemo(
    () =>
      JSON.stringify({
        items: toPreviewItems(items, includeNote),
        updateKind,
        effectiveDate
      }),
    [items, updateKind, effectiveDate, includeNote]
  );

  useEffect(() => {
    if (!contractId) return;
    let isCurrent = true;
    setIsRefreshing(true);

    const timer = setTimeout(() => {
      previewRef.current.mutate(
        {
          pathParams: { contractId },
          data: JSON.parse(requestBody)
        },
        {
          onSuccess: (response) => {
            if (!isCurrent) return;
            setValues(response.calculatedValues);
            setChangeSet(response.changeSet ?? null);
            setIsRefreshing(false);
            setIsStale(false);
            hasSucceededRef.current = true;
          },
          onError: () => {
            if (!isCurrent) return;
            setIsRefreshing(false);
            setIsStale(hasSucceededRef.current);
          }
        }
      );
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [contractId, requestBody]);

  return {
    values: values ?? fromContract(contract),
    changeSet,
    isRefreshing,
    isStale
  };
};
